import { ref, onUnmounted, type Ref } from 'vue'
import CryptoJS from 'crypto-js'
import { uploadImage } from '@/api/service'
import { getSimpleDictDataList } from '@/api/config'
import type { Response } from '@/types/http'
import type { UploadFile } from '@/types/service'
import relativeToast from '@/utils/relativeToast'
import toast from '@/utils/toast'
import { useI18n } from 'vue-i18n'

/**
 * 提取文件名和扩展名
 */
function getFileNameAndFormat(file: File): { name: string; format: string } {
  if (!file || !file.name) {
    console.error('Invalid file object')
    return { name: '', format: '' }
  }

  const fileName = file.name
  const fileExtension = fileName.split('.').pop()?.toLowerCase() || ''
  const baseName = fileName.substring(0, fileName.lastIndexOf('.')) || fileName

  return {
    name: baseName,
    format: fileExtension
  }
}

/**
 * SHA-1 哈希算法生成唯一文件名
 */
function simpleHash(str: string): string {
  return CryptoJS.SHA1(str).toString(CryptoJS.enc.Hex)
}

/**
 * 将 Blob 转换为 File 对象
 */
function ensureFile(input: Blob | File, filename: string): File {
  return input instanceof File
    ? input
    : new File([input], filename, {
        type: input.type,
        lastModified: Date.now()
      })
}

/**
 * Canvas toBlob Promise 包装
 */
function toBlobPromise(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('压缩失败'))), type, quality)
  })
}

/**
 * 图片上传 Hook
 */
export function useImageUpload(
  fileInputRef: Ref<HTMLInputElement | null>,

  container?: HTMLElement | Ref<HTMLElement | undefined>
) {
  const fileList = ref<UploadFile[]>([])
  const limitCount = 3
  const maxSize = ref<number>(500)
  const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  const uploadCount = ref<number>(0)
  const isLoadingMaxSize = ref<boolean>(false)
  const { t } = useI18n()
  /**
   * 获取容器元素
   */
  const getContainer = (): HTMLElement | undefined => {
    if (!container) return undefined
    return container instanceof HTMLElement ? container : container.value
  }

  /**
   * 显示相对定位 Toast
   */
  const showToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const containerEl = getContainer()
    if (containerEl) {
      relativeToast[type](message, {
        container: containerEl,
        position: 'center'
      })
    } else {
      // 如果没有 container，降级使用全局 toast
      toast[type](message)
    }
  }

  /**
   * 获取文件大小限制配置
   */
  const fetchUploadMaxSize = async () => {
    isLoadingMaxSize.value = true
    try {
      const res = await getSimpleDictDataList()
      const uploadSizeItem = res?.find((v) => v.label === 'APP:IMAGE:UPLOAD:SIZE')
      if (uploadSizeItem?.value) {
        maxSize.value = Number(uploadSizeItem.value) || 500
      }
    } catch (error) {
      console.error('Failed to fetch upload max size:', error)
      maxSize.value = 500
    } finally {
      isLoadingMaxSize.value = false
    }
  }

  /**
   * 初始化时获取配置
   */
  fetchUploadMaxSize()

  /**
   * 触发文件选择
   */
  const triggerFileInput = () => {
    if (fileList.value.length >= limitCount) return
    fileInputRef.value?.click()
  }

  /**
   * 判断文件格式是否允许
   */
  const isAllowedFormat = (file: File): boolean => {
    return allowedFormats.includes(file.type)
  }

  /**
   * 使用 Canvas 压缩图片
   */
  const compressImageWithCanvas = async (
    file: File,
    maxSizeKB: number,
    maxWidthOrHeight = 1280
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = async () => {
        try {
          const canvas = document.createElement('canvas')
          let { width, height } = img

          if (width > height && width > maxWidthOrHeight) {
            height = (height * maxWidthOrHeight) / width
            width = maxWidthOrHeight
          } else if (height > width && height > maxWidthOrHeight) {
            width = (width * maxWidthOrHeight) / height
            height = maxWidthOrHeight
          }

          canvas.width = width
          canvas.height = height

          const ctx = canvas.getContext('2d')
          if (!ctx) {
            reject(new Error('Canvas context 获取失败'))
            return
          }

          ctx.drawImage(img, 0, 0, width, height)

          const tryCompress = async (quality: number): Promise<void> => {
            try {
              const blob = await toBlobPromise(canvas, 'image/jpeg', quality)
              const sizeKB = blob.size / 1024

              if (sizeKB <= maxSizeKB || quality <= 0.1) {
                resolve(ensureFile(blob, file.name))
              } else {
                await tryCompress(quality - 0.1)
              }
            } catch (error) {
              reject(error)
            }
          }

          await tryCompress(0.9)
        } catch (error) {
          reject(error)
        }
      }

      img.onerror = () => reject(new Error('图片加载失败'))
      img.src = URL.createObjectURL(file)
    })
  }

  /**
   * 压缩并上传图片
   */
  const compressAndUpload = async (rawFile: File): Promise<void> => {
    const originalSizeKB = rawFile.size / 1024
    let finalFile = rawFile

    if (originalSizeKB > maxSize.value) {
      try {
        const compressed = await compressImageWithCanvas(rawFile, maxSize.value)
        finalFile = compressed
      } catch (err) {
        console.error('图片压缩失败', err)
        showToast(t('图片压缩失败'), 'error')
        return
      }
    }

    const previewUrl = URL.createObjectURL(finalFile)

    const uploadFile: UploadFile = {
      ...finalFile,
      file: finalFile,
      previewUrl,
      status: 'uploading',
      message: '',
      resUrl: ''
    } as UploadFile

    fileList.value.push(uploadFile)
    await httpUpload(uploadFile)
  }

  /**
   * 上传图片到服务器
   */
  const httpUpload = async (file: UploadFile): Promise<void> => {
    uploadCount.value++

    const formData = new FormData()
    formData.append('file', file.file)

    const y = new Date().getFullYear()
    let m: number | string = new Date().getMonth() + 1
    m = m > 9 ? m : '0' + m

    const { name, format } = getFileNameAndFormat(file.file)
    if (!name || !format) {
      uploadCount.value--
      handleUploadError(file)
      return
    }

    const filePathName = `front_h5/${y}_${m}/images/feedback/${simpleHash(`${name}_${Date.now()}`)}.${format}`
    formData.append('path', filePathName)

    try {
      const res = await uploadImage(formData, (progress: number) => {
        console.log('Upload progress:', progress)
      })

      handleUploadResponse(res, file)
    } catch (err) {
      console.error('上传失败', err)
      handleUploadError(file)
    } finally {
      uploadCount.value--
    }
  }

  /**
   * 更新文件状态
   */
  const updateFile = (target: UploadFile, patch: Partial<UploadFile>): void => {
    const index = fileList.value.findIndex((f) => f.previewUrl === target.previewUrl)
    if (index === -1) return

    if (fileList.value[index].previewUrl) {
      URL.revokeObjectURL(fileList.value[index].previewUrl)
    }

    const newPreviewUrl =
      patch.status === 'done'
        ? URL.createObjectURL(fileList.value[index].file)
        : fileList.value[index].previewUrl

    fileList.value[index] = {
      ...fileList.value[index],
      ...patch,
      previewUrl: newPreviewUrl
    } as UploadFile

    fileList.value = [...fileList.value]
  }

  /**
   * 处理上传成功响应
   */
  const handleUploadResponse = (res: Response<string>, file: UploadFile): void => {
    if (res.code === 200) {
      updateFile(file, {
        status: 'done',
        message: '',
        resUrl: res.data as string
      })
      showToast(t('图片上传成功'), 'success')
    } else {
      showToast(res.msg || t('上传失败'), 'error')
      handleUploadError(file)
    }
  }

  /**
   * 处理上传失败
   */
  const handleUploadError = (file: UploadFile): void => {
    fileList.value = fileList.value.filter((f) => f.previewUrl !== file.previewUrl)
  }

  /**
   * 处理文件选择
   */
  const handleFileChange = async (e: Event): Promise<void> => {
    const input = e.target as HTMLInputElement
    const files = input.files
    if (!files) return

    const selectedFiles = Array.from(files).slice(0, limitCount - fileList.value.length)

    for (const rawFile of selectedFiles) {
      if (!isAllowedFormat(rawFile)) {
        showToast(t('customer.imageUpload.invalidFormat'), 'warning')
        continue
      }

      await compressAndUpload(rawFile)
    }

    input.value = ''
  }

  /**
   * 删除文件
   */
  const removeFile = (index: number): void => {
    const removed = fileList.value.splice(index, 1)[0]
    if (removed?.previewUrl) {
      URL.revokeObjectURL(removed.previewUrl)
    }
  }

  /**
   * 清理资源
   */
  onUnmounted(() => {
    fileList.value.forEach((file) => {
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl)
      }
    })
    fileList.value = []
  })

  return {
    fileList,
    maxSize,
    uploadCount,
    triggerFileInput,
    removeFile,
    handleFileChange,
    isLoadingMaxSize
  }
}

export type UseImageUploadReturn = ReturnType<typeof useImageUpload>
